import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Optional } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';

import type { MethodTypeAcquisitionField } from '../../../state/method-type.state';

/**
 * Self-contained dialog for creating a single asset field.
 *
 * Collects a `displayName` / `fieldPath` pair and closes with the resulting
 * {@link MethodTypeAcquisitionField} on save, or `undefined` on cancel. It owns
 * no shared state — the consumer (the dumb asset-fields manager) opens it and
 * stages whatever it returns. Save is blocked while either field is blank.
 */
@Component({
  selector: 'ts-method-asset-field-dialog',
  templateUrl: './method-asset-field-dialog.component.html',
  styleUrls: ['./method-asset-field-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, TranslateModule],
})
export class MethodAssetFieldDialogComponent {
  public readonly form = new FormGroup({
    displayName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    fieldPath: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  constructor(
    @Optional()
    public readonly dialogRef: MatDialogRef<
      MethodAssetFieldDialogComponent,
      MethodTypeAcquisitionField | undefined
    > | null
  ) {}

  /** A field is only savable when both inputs hold non-blank text. */
  public get canSave(): boolean {
    return this.trimmedDisplayName.length > 0 && this.trimmedFieldPath.length > 0;
  }

  private get trimmedDisplayName(): string {
    return this.form.controls.displayName.value.trim();
  }

  private get trimmedFieldPath(): string {
    return this.form.controls.fieldPath.value.trim();
  }

  public cancel(): void {
    this.dialogRef?.close(undefined);
  }

  public save(): void {
    if (!this.canSave) {
      return;
    }
    this.dialogRef?.close({
      displayName: this.trimmedDisplayName,
      fieldPath: this.trimmedFieldPath,
    });
  }
}
